import { useMemo } from "react";
import { format } from "date-fns";
import { useQuery } from "@tanstack/react-query";
import { DEFAULT_DATE_FORMAT, type DateFormat } from "@repo/shared";

import { organizationMembershipsQueryOptions } from "@/features/qlik-connections/api";
import { getStoredActiveOrganizationId } from "@/lib/api-client";
import { useLocale } from "@/hooks/use-locale";

type DateInput = string | number | Date;

/** The active organization's date-presentation preference. */
export function useOrgDateFormat(): DateFormat {
  const { data: memberships = [] } = useQuery(organizationMembershipsQueryOptions);
  const activeId = getStoredActiveOrganizationId();
  const active = memberships.find((m) => m.id === activeId) ?? memberships[0];
  return active?.dateFormat ?? DEFAULT_DATE_FORMAT;
}

/**
 * Formatters bound to the active org's date format and the current i18n locale.
 * `formatDate` → date only; `formatDateTime` → date + 24h time with seconds.
 * The org format enum values double as date-fns patterns.
 */
export function useFormatDate() {
  const pattern = useOrgDateFormat();
  const { dateFnsLocale } = useLocale();

  return useMemo(() => {
    const opts = { locale: dateFnsLocale };
    const toDate = (d: DateInput) => (d instanceof Date ? d : new Date(d));
    return {
      dateFormat: pattern,
      formatDate: (d: DateInput) => format(toDate(d), pattern, opts),
      formatDateTime: (d: DateInput) => format(toDate(d), `${pattern} HH:mm:ss`, opts),
    };
  }, [pattern, dateFnsLocale]);
}
